import _ from 'lodash';
import * as Settlements from '@/types/models/settlement';
import { accountHasOfficePermission } from '@/store/permissions';
import moment from 'moment';
import { mapGetters } from 'vuex';

export default {
  methods: {
    ...mapGetters('profiles', {
      findCompanyAccount: 'findCompanyAccount',
    }),
    // does the provided company pay for xero?
    isXeroEnabled(company) {
      return true || company;
    },
    xeroDirectFor(company) {
      return _.get(company, 'plugins.invoicing.xero', null);
    },
    // has the provided company ever provided Xero credentials?
    isXeroConnected(company) {
      return !!this.xeroDirectFor(company);
    },
    // has the provided company provided current Xero credentials?
    isXeroCredentialsValid(company) {
      if (this.isXeroConnected(company)) {
        const ts = _.get(company, 'plugins.invoicing.xero.credentialExpires', null);
        if (ts) {
          const expiry = moment(ts, moment.ISO_8601);
          return moment().isBefore(expiry);
        }
      }
      return false;
    },
    daysUntilXeroCredentialsExpire(company) {
      const now = moment();
      const ts = _.get(company, 'plugins.invoicing.xero.credentialExpires', null);
      if (ts) {
        const expiry = moment(ts, moment.ISO_8601);
        return expiry.diff(now, 'day');
      }

      return 0;
    },
    shouldUseXero(company) {
      return this.isXeroEnabled(company) && this.isXeroCredentialsValid(company);
    },
    currentUserHasCreateSettlementInvoicePermission(settlement) {
      const invoice = Settlements.invoice(settlement);
      const paidToCompanyAccount = this.findCompanyAccount()(invoice.paidTo.id);

      return this.isLtnAdmin || accountHasOfficePermission(paidToCompanyAccount);
    },
    currentUserHasCreateTransactionInvoicePermission(txn) {
      const customerCompanyAccount = this.findCompanyAccount()(txn.vendor.companyId);

      return this.isLtnAdmin || accountHasOfficePermission(customerCompanyAccount);
    },
    xeroPaymentTerms(companyId) {
      return this.xdSalesTerms[companyId];
    },
    xeroAccounts(companyId) {
      return this.xdAccounts[companyId];
    },
    customerCompanyName(customer) {
      return customer.xdCompanyName;
    },
    customerContactName(customer) {
      return customer.xdName;
    },
    customerDisplayName(customer) {
      return customer.xdDisplayName || customer.xdCompanyName;
    },
    /**
     * A vendor looks like this:
     {
       xdCompanyId: "1234",
       xdDisplayName: "*3 Brand Cattle Co.",
       xdName: "3 Brands",
       selectDisplay: "3 Brands"             <=== this is added by this function as a matter of consistency
     }
     */
    xeroVendors(companyId) {
      const vendors = this.xdVendors[companyId] || [];
      // eslint-disable-next-line no-return-assign,no-param-reassign
      return vendors.map((vendor) => {
        // eslint-disable-next-line no-param-reassign
        vendor.selectDisplay = this.customerDisplayName(vendor);
        return vendor;
      });
    },
    xeroCustomers(companyId) {
      const customers = this.xdCustomers[companyId] || [];
      return customers.map((customer) => {
        // eslint-disable-next-line no-param-reassign
        customer.selectDisplay = this.customerDisplayName(customer);
        return customer;
      });
    },
    xeroSalesItems(companyId) {
      return this.xdSalesItems[companyId] || [];
    },
    xeroTaxCodes(companyId) /* : [TaxCode] */ {
      return this.xdTaxCodes[companyId] || [];
    },
    xeroSalesTaxCodes(companyId) /* : [TaxCode] */ {
      return _.chain(this.xeroTaxCodes(companyId))
        .filter(tc => _.get(tc, 'salesTaxRateList.taxRateDetail', []).length > 0)
        .value();
    },
    xeroPurchaseTaxCodes(companyId) /* : [TaxCode] */ {
      return _.chain(this.xeroTaxCodes(companyId))
        .filter(tc => _.get(tc, 'purchaseTaxRateList.taxRateDetail', []).length > 0)
        .value();
    },

    xeroCompanyCollectsTax(company) {
      const xd = this.xeroDirectFor(company);
      return _.get(xd, 'configuredToCollectTax', false);
    },
    defaultCompanyXeroTaxCodes(company) {
      const salesCodeId = this.defaultCompanyXeroInvoiceSalesTaxCode(company) || null;
      const purchaseCodeId = this.defaultCompanyXeroBillPurchaseTaxCode(company) || null;

      let salesCode = { id: null };
      let purchaseCode = { id: null };

      if (salesCodeId) {
        const salesCodes = this.xeroSalesTaxCodes(company.id);
        salesCode = _.chain(salesCodes)
          .filter(tc => salesCodeId)
          .filter(tc => tc.id.toString() === salesCodeId.toString())
          .first()
          .value() || { id: null };
      }

      if (purchaseCodeId) {
        const purchaseCodes = this.xeroPurchaseTaxCodes(company.id);
        purchaseCode = _.chain(purchaseCodes)
          .filter(tc => purchaseCodeId)
          .filter(tc => tc.id.toString() === purchaseCodeId.toString())
          .first()
          .value() || { id: null };
      }

      return {
        sales: salesCode,
        purchase: purchaseCode,
      };
    },
    defaultCompanyXeroInvoiceSalesTaxCode(company) {
      const xd = this.xeroDirectFor(company);
      return _.get(xd, 'exportDefaults.invoices.taxCode', null);
    },
    defaultCompanyXeroBillPurchaseTaxCode(company) {
      const xd = this.xeroDirectFor(company);
      return _.get(xd, 'exportDefaults.bills.taxCode', null);
    },
    defaultCompanyXeroInvoiceSalesItem(company) {
      const xd = this.xeroDirectFor(company);
      const id = _.get(xd, 'exportDefaults.invoices.salesItemId');
      if (id) {
        return _.find(this.xeroSalesItems(company.id), e => e.itemId === id);
      }

      return null;
    },
    defaultCompanyXeroCustomerInvoiceTerms(company) {
      const xd = this.xeroDirectFor(company);
      const id = _.get(xd, 'exportDefaults.invoices.paymentTermsId');
      if (id) {
        return _.find(this.xeroPaymentTerms(company.id), e => e.id === id);
      }

      return null;
    },
    defaultCompanyXeroVendorBillTerms(company) {
      const xd = this.xeroDirectFor(company);
      const id = _.get(xd, 'exportDefaults.bills.paymentTermsId');
      if (id) {
        return _.find(this.xeroPaymentTerms(company.id), e => e.id === id);
      }

      return null;
    },
    defaultCompanyXeroTransportationFeeExpenseAccount(company) {
      const xd = this.xeroDirectFor(company);
      const id = _.get(xd, 'exportDefaults.bills.transportationExpenseAccountId');
      if (id) {
        return _.find(this.xeroAccounts(company.id), e => e.accountId === id);
      }

      return null;
    },
    defaultCompanyXeroDispatchFeeIncomeAccount(company) {
      const xd = this.xeroDirectFor(company);
      const id = _.get(xd, 'exportDefaults.bills.dispatchFeeIncomeAccountId');
      if (id) {
        return _.find(this.xeroAccounts(company.id), e => e.accountId === id);
      }

      return null;
    },
    defaultCompanyXeroTrailerLeaseFeeIncomeAccount(company) {
      const xd = this.xeroDirectFor(company);
      const id = _.get(xd, 'exportDefaults.bills.trailerLeaseFeeIncomeAccountId');
      if (id) {
        return _.find(this.xeroAccounts(company.id), e => e.accountId === id);
      }

      return null;
    },
    loadCompanyXeroInfo(companyId) {
      this.refreshCompanyXeroInfo(companyId);
      this.refreshXeroSalesItems(companyId);
      this.refreshXeroAccounts(companyId);
      this.refreshXeroContacts(companyId);
      this.refreshXeroPaymentTerms(companyId);
      this.refreshXeroTaxCodes(companyId);
    },
    refreshCompanyXeroInfo(companyId) {
      this.$store.dispatch('xero/reloadCompanyInfo', companyId);
    },
    refreshXeroSalesItems(companyId) {
      this.$store.dispatch('xero/getXeroSalesItems', companyId);
    },
    refreshXeroPaymentTerms(companyId) {
      this.$store.dispatch('xero/getTerms', companyId);
    },
    refreshXeroAccounts(companyId) {
      this.$store.dispatch('xero/getXeroAccounts', companyId);
    },
    refreshXeroContacts(companyId) {
      this.$store.dispatch('xero/getXeroContacts', companyId);
    },
    refreshXeroTaxCodes(companyId) {
      this.$store.dispatch('xero/getXeroTaxCodes', companyId);
    },
    isCompanyXeroInfoLoading(companyId) {
      return this.customersStatus[companyId] ||
             this.accountsStatus[companyId] ||
             this.vendorsStatus[companyId] ||
             this.salesTermsStatus[companyId] ||
             this.salesItemsStatus[companyId] ||
             this.taxCodesStatus[companyId] ||
             false;
    },

    xeroVendorDrivers(companyEmployees, vendors) {
      const vendorsByXdId = _.groupBy(vendors, v => v.xdCompanyId);

      const drivers = _.filter(companyEmployees, ee => ee.driverProfile);
      const driverVendors = _.filter(drivers, d => d.xeroVendorId);

      _.forEach(driverVendors, (dv) => {
        const vendorList = vendorsByXdId[dv.xeroVendorId];
        if (vendorList && vendorList.length > 0) {
          // eslint-disable-next-line no-param-reassign, no-underscore-dangle
          dv.__vendor = vendorList[0];
        }
      });

      return driverVendors;
    },
  },
  computed: {
    ...mapGetters('profiles', {
      isLtnAdmin: 'isLtnAdmin',
    }),
    ...mapGetters('xero', {
      xdContacts: 'contacts',
      contactsStatus: 'contactsStatus',
      xdAccounts: 'accounts',
      accountsStatus: 'accountsStatus',
      xdSalesTerms: 'salesTerms',
      salesTermsStatus: 'salesTermsStatus',
      xdSalesItems: 'salesItems',
      salesItemsStatus: 'salesItemsStatus',
      xdTaxCodes: 'taxCodes',
      taxCodesStatus: 'taxCodesStatus',
    }),
    xeroSubscriptionRequiredForBilling() {
      return 'Exporting transactions requires the Billing features in Xero, at least a Standard subscription';
    },
  },
};



// WEBPACK FOOTER //
// ./src/mixins/xeroMixin.js